import { getErrorMessage } from "@shared/src/utils";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { useCallback, useEffect } from "react";
import { ReleaseConfirmationDialog } from "@shared/src/components/modules/p2p/order/execution/ReleaseConfirmationDialog";
import { OrderExecutor, OrderExecutorArgs } from "@shared/src/components/modules/p2p/order/execution/types";
import { KycRestriction } from "@shared/src/components/common/dialogs/KycRestriction";
import { executeOrder as sdkExecuteOrder, OrderExecutorArgs as SdkOrderExecutorArgs } from "@sdk/orderExecutor";
import { IncompatibleNetworkError, KYCError } from "@sdk/errors";
import { useHostStore } from "@shared/src/stores/useHostStore";
import useWriteContractWithToast from "@shared/src/hooks/use-write-contract-with-toast";

export default function useOrderExecutor(): OrderExecutor {
  const { sdk, connectorId } = useHostStore();
  const { showModal, closeModal, showNetworkMismatchModal } = useModalStore();
  const { writeContractAsync } = useWriteContractWithToast();

  return useCallback(
    async (args: OrderExecutorArgs): Promise<{ orderHash?: string; error?: string }> => {
      const getSuccessMessage = () => {
        if (args.action === "acceptOrder") return "Order Accepted Successfully";
        if (args.action === "cancelOrder") return "Order Cancelled Successfully";
        if (args.action === "payOrder") return "Order Paid Successfully";
        if (args.action === "releaseOrder") return "Order Released Successfully";
        if (args.action === "appealOrder")
          return "Please join our support center on Telegram and provide your order details: https://t.me/+wLyQQTNHwM9jNmE8";
        return "Successfull";
      };

      // Helper to map local action names to SDK action names
      const mapArgsToSdk = (local: OrderExecutorArgs): SdkOrderExecutorArgs => {
        switch (local.action) {
          case "create":
            return {
              action: "createOrder",
              amount: local.amount!,
              ad: local.ad,
              paymentConfig: local.paymentConfig,
              paymentMethod: local.paymentMethod!,
              orderData: undefined,
            };
          case "payOrder":
            return {
              action: "payOrder",
              orderData: local.orderData,
              paymentConfig: local.paymentConfig,
            };
          case "releaseOrder":
            return {
              action: "releaseOrder",
              orderData: local.orderData,
            };
          case "cancelOrder":
            return {
              action: "cancelOrder",
              orderData: local.orderData,
            };
          case "acceptOrder":
            return {
              action: "acceptOrder",
              orderData: local.orderData,
            };
          case "appealOrder":
            return {
              action: "appealOrder",
              orderData: (local as any).orderData,
            };
          default:
            throw new Error(`Unsupported action: ${(local as any).action}`);
        }
      };

      // Custom contract writer that delegates to useWriteContractWithToast
      const contactWriter = async (params: any) => {
        const result = await writeContractAsync(
          {
            waitForReceipt: params.waitForReceipt ?? false,
            loadingMessage: "Processing",
            successMessage: "Transaction Successful",
            simulateFirst: false,
          },
          {
            abi: params.abi,
            address: params.address,
            functionName: params.functionName,
            args: params.args,
          },
        );

        return {
          hash: result.txHash,
          receipt: result.receipt,
        };
      };

      // Handle release confirmation UI before executing order
      if (args.action === "releaseOrder") {
        try {
          await new Promise((resolve, reject) =>
            showModal({
              type: "warning",
              title: "Confirm Payment Received",
              message: (
                <ReleaseConfirmationDialog
                  onConfirm={async () => {
                    resolve(true);
                    closeModal();
                  }}
                  onCancel={() => {
                    reject(false);
                    closeModal();
                  }}
                />
              ),
            }),
          );
        } catch {
          return { error: "User cancelled" };
        }
      }

      // Show initial loading modal
      showModal({ type: "loading", title: "Processing", message: "Please wait...", preventClose: true });

      try {
        const sdkArgs = mapArgsToSdk(args);
        if (!sdk) return { error: "SDK not initialized, creating order" };
        const result = await sdkExecuteOrder(sdk, sdkArgs, {
          contactWriter,
          onMessage: () => {},
        });

        if (result.error instanceof KYCError) {
          showModal({
            type: "warning",
            message: <KycRestriction closeModal={closeModal} />,
          });
          return { error: result.error.message };
        }
        if (result.error instanceof IncompatibleNetworkError) {
          const data = result.error.data;

          const walletClient = sdk?.auth.getWalletClient();
          if (walletClient) {
            showNetworkMismatchModal(data?.expectedChainId as number);
            return { error: result.error.message };
          }

          showModal({ type: "error", message: result.error.message });
          return { error: result.error.message };
        }

        if (result.error) {
          showModal({ type: "error", message: result.error.message });
          return { error: result.error.message };
        }

        showModal({ type: "success", message: getSuccessMessage(), autoClose: true });

        return { orderHash: (result.data as any)?.id };
      } catch (error: any) {
        console.error(error);
        closeModal();
        showModal({ type: "error", message: getErrorMessage(error) });
        return { error: getErrorMessage(error) };
      }
    },
    [writeContractAsync, showModal, closeModal, sdk],
  );
}
